import { useEffect } from 'react';

/**
 * Traps keyboard focus inside a modal/overlay element.
 *
 * @param {React.RefObject} oModalRef - Ref to the container element.
 * @param {object} [oOptions] - Optional configuration.
 * @param {boolean} [oOptions.bQueryDynamically=false] - When true, focusable
 *   elements are re-queried on every Tab keydown so dynamically loaded content
 *   is included. When false, elements are captured once on mount.
 * @param {function} [oOptions.fnOnEscape] - Called when the user presses
 *   Escape. If omitted, Escape is ignored.
 * @param {boolean} [oOptions.bAutoFocus=true] - When true, the first focusable
 *   element is focused on mount via a double-rAF to wait for paint.
 * @param {boolean} [oOptions.bEnabled=true] - When false, the hook is a no-op.
 *   Useful for conditionally enabling focus trapping (e.g. mobile nav).
 */
const useTrapFocusInsideModal = (oModalRef, oOptions = {}) => {
  const {
    bQueryDynamically = false,
    fnOnEscape,
    bAutoFocus = true,
    bEnabled = true,
  } = oOptions;

  useEffect(() => {
    if (!bEnabled || !oModalRef.current) return undefined;

    const oContainer = oModalRef.current;
    const sFocusableSelector = 'a, button, input, textarea';

    const fnGetFocusableElements = () =>
      oContainer.querySelectorAll(sFocusableSelector);

    // Auto-focus the first focusable element after the browser paints.
    // Double rAF ensures any display transition has been painted.
    let rafId;
    if (bAutoFocus) {
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          const aElements = fnGetFocusableElements();
          aElements[0]?.focus({ focusVisible: true });
        });
      });
    }

    // Capture elements once for static content
    const aStaticElements = bQueryDynamically ? null : fnGetFocusableElements();

    const fnHandleKeyDown = (oEvent) => {
      if (oEvent.key === 'Escape' && fnOnEscape) {
        fnOnEscape();
        return;
      }

      if (oEvent.key !== 'Tab') return;

      const aFocusable = bQueryDynamically
        ? fnGetFocusableElements()
        : aStaticElements;
      const oFirst = aFocusable[0];
      const oLast = aFocusable[aFocusable.length - 1];

      if (oEvent.shiftKey && document.activeElement === oFirst) {
        oEvent.preventDefault();
        oLast.focus();
      } else if (!oEvent.shiftKey && document.activeElement === oLast) {
        oEvent.preventDefault();
        oFirst.focus();
      }
    };

    document.addEventListener('keydown', fnHandleKeyDown);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', fnHandleKeyDown);
    };
  }, [oModalRef, bQueryDynamically, fnOnEscape, bAutoFocus, bEnabled]);
};

export default useTrapFocusInsideModal;
